/**
 * The formulas of table columns (`quantity * coalesce(months, 1) * unit_price`), evaluated as
 * Core evaluates them (App\Domains\Core\Formula): numbers, fields, + - * / %, comparisons,
 * and the functions a row needs. Arithmetic with something that is not a number gives null,
 * a division by zero gives null — so the editor's sums match the document's.
 */
const FUNCTIONS = {
  if: (a) => (truthy(a[0]) ? a[1] : a[2] ?? null),
  coalesce: (a) => a.find((v) => v !== null && v !== undefined && v !== '') ?? null,
  round: (a) => (isNum(a[0]) ? round(Number(a[0]), Number(a[1] ?? 0)) : null),
  floor: (a) => (isNum(a[0]) ? Math.floor(Number(a[0])) : null),
  ceil: (a) => (isNum(a[0]) ? Math.ceil(Number(a[0])) : null),
  abs: (a) => (isNum(a[0]) ? Math.abs(Number(a[0])) : null),
  min: (a) => (a.filter(isNum).length ? Math.min(...a.filter(isNum).map(Number)) : null),
  max: (a) => (a.filter(isNum).length ? Math.max(...a.filter(isNum).map(Number)) : null),
}

const isNum = (v) => v !== null && v !== '' && v !== undefined && typeof v !== 'boolean' && !Number.isNaN(Number(v))
const truthy = (v) => !(v === null || v === undefined || v === false || v === 0 || v === '' || v === '0')
function round(v, digits) {
  const f = 10 ** digits
  return Math.round((v + Number.EPSILON) * f) / f
}

function tokenize(text) {
  const out = []
  const re = /\s*(?:(\d+(?:\.\d+)?)|([A-Za-z_][A-Za-z0-9_.]*)|('(?:[^'\\]|\\.)*')|(<=|>=|!=|[-+*/%(),<>=]))/y
  let m
  re.lastIndex = 0
  while (re.lastIndex < text.length) {
    const at = re.lastIndex
    m = re.exec(text)
    if (!m) {
      if (/^\s*$/.test(text.slice(at))) break
      throw new Error(`Bad formula at ${at}`)
    }
    if (m[1]) out.push({ t: 'num', v: Number(m[1]) })
    else if (m[2]) out.push({ t: 'id', v: m[2] })
    else if (m[3]) out.push({ t: 'str', v: m[3].slice(1, -1).replace(/\\(.)/g, '$1') })
    else out.push({ t: 'op', v: m[4] })
  }
  return out
}

/** The formula as a function of a row's values; a formula that does not parse gives null. */
export function compile(formula) {
  let tokens
  try {
    tokens = tokenize(String(formula || ''))
  } catch {
    return () => null
  }
  let i = 0
  const peek = () => tokens[i]
  const take = (v) => (peek() && peek().v === v ? tokens[i++] : null)

  // comparison < additive < multiplicative < unary < primary
  function comparison() {
    let left = additive()
    for (;;) {
      const op = ['=', '!=', '<', '<=', '>', '>='].find((o) => peek()?.t === 'op' && peek().v === o)
      if (!op) return left
      i++
      const a = left
      const b = additive()
      left = (r) => compare(op, a(r), b(r))
    }
  }
  function additive() {
    let left = multiplicative()
    for (;;) {
      const op = peek()?.t === 'op' && ['+', '-'].includes(peek().v) ? tokens[i++].v : null
      if (!op) return left
      const a = left
      const b = multiplicative()
      left = (r) => arith(op, a(r), b(r))
    }
  }
  function multiplicative() {
    let left = unary()
    for (;;) {
      const op = peek()?.t === 'op' && ['*', '/', '%'].includes(peek().v) ? tokens[i++].v : null
      if (!op) return left
      const a = left
      const b = unary()
      left = (r) => arith(op, a(r), b(r))
    }
  }
  function unary() {
    if (take('-')) {
      const a = unary()
      return (r) => (isNum(a(r)) ? -Number(a(r)) : null)
    }
    if (peek()?.t === 'id' && peek().v === 'not') {
      i++
      const a = unary()
      return (r) => !truthy(a(r))
    }
    return primary()
  }
  function primary() {
    const tok = tokens[i++]
    if (!tok) throw new Error('Unexpected end')
    if (tok.t === 'num' || tok.t === 'str') return () => tok.v
    if (tok.t === 'op' && tok.v === '(') {
      const inner = comparison()
      if (!take(')')) throw new Error('Missing )')
      return inner
    }
    if (tok.t === 'id') {
      const name = tok.v.toLowerCase()
      if (name === 'null') return () => null
      if (name === 'true' || name === 'false') return () => name === 'true'
      if (take('(')) {
        const args = []
        if (!take(')')) {
          do args.push(comparison())
          while (take(','))
          if (!take(')')) throw new Error('Missing )')
        }
        const fn = FUNCTIONS[name]
        return (r) => (fn ? fn(args.map((a) => a(r))) : null)
      }
      return (r) => r?.[tok.v] ?? null
    }
    throw new Error(`Unexpected ${tok.v}`)
  }

  let fn
  try {
    fn = comparison()
    if (i < tokens.length) throw new Error('Trailing input')
  } catch {
    return () => null
  }
  return (row) => {
    try {
      const v = fn(row || {})
      return typeof v === 'number' && !Number.isFinite(v) ? null : v
    } catch {
      return null
    }
  }
}

function arith(op, a, b) {
  if (!isNum(a) || !isNum(b)) return null
  const x = Number(a)
  const y = Number(b)
  if (op === '+') return x + y
  if (op === '-') return x - y
  if (op === '*') return x * y
  if (op === '/') return y === 0 ? null : x / y
  return y === 0 ? null : x % y
}
function compare(op, a, b) {
  const both = isNum(a) && isNum(b)
  const x = both ? Number(a) : String(a ?? '')
  const y = both ? Number(b) : String(b ?? '')
  return { '=': x === y, '!=': x !== y, '<': x < y, '<=': x <= y, '>': x > y, '>=': x >= y }[op]
}

const cache = new Map()
/** Evaluates a formula against a row's values (compiled once per formula). */
export function evaluate(formula, values) {
  if (!cache.has(formula)) cache.set(formula, compile(formula))
  return cache.get(formula)(values)
}
